// src/components/reports/EnhancedReportsGenerator.jsx
import React, { useState, useCallback, useMemo } from 'react';
import { 
  FileText, 
  Users, 
  UserCheck, 
  BarChart3, 
  Loader2, 
  CheckCircle2,
  Plus,
  X,
  TrendingUp,
  Target,
  Brain,
  Calendar,
  Book,
  MessageSquare,
  ArrowLeftRight,
  PieChart,
  Activity
} from 'lucide-react';
import { AssessmentService } from '../../services/taskService';
import { useAuth } from '../../contexts/AuthContext';
import ReportDisplay from './ReportDisplay';
import TaskComparisonChart from './TaskComparisonChart';
import { ReportsService } from '../../services/reportsService';

const EnhancedReportsGenerator = () => {
  const { userProfile } = useAuth();
  
  // Form state
  const [reportType, setReportType] = useState('');
  const [selectedGrade, setSelectedGrade] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedLearner, setSelectedLearner] = useState('');
  const [reportContext, setReportContext] = useState('');
  
  // Comparison state
  const [enableComparison, setEnableComparison] = useState(false);
  const [comparisonTask1, setComparisonTask1] = useState('');
  const [comparisonTask2, setComparisonTask2] = useState('');
  const [comparisonScope, setComparisonScope] = useState('learner'); // 'learner' or 'grade'
  
  // Data state
  const [availableTasks, setAvailableTasks] = useState([]);
  const [availableLearners, setAvailableLearners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingLearners, setLoadingLearners] = useState(false);
  const [generatedReport, setGeneratedReport] = useState(null);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Available grades and subjects
  const grades = ['Grade R', 'Grade 1', 'Grade 2', 'Grade 3', 'Grade 4', 'Grade 5', 'Grade 6', 'Grade 7', 'Grade 8', 'Grade 9', 'Grade 10', 'Grade 11', 'Grade 12'];
  const subjects = ['Mathematics', 'English', 'Afrikaans', 'Physical Sciences', 'Life Sciences', 'Geography', 'History', 'Accounting', 'Business Studies', 'Economics', 'Life Orientation', 'Technology', 'Natural Sciences', 'Social Sciences', 'Arts and Culture', 'Creative Arts'];

  // Report type definitions with enhanced options
  const reportTypes = useMemo(() => [
    {
      id: 'grade',
      label: 'Grade Overview',
      description: 'Comprehensive grade-level performance analysis across all subjects',
      icon: Users,
      color: 'bg-blue-50 border-blue-200 text-blue-700',
      supportsComparison: false
    },
    {
      id: 'subject',
      label: 'Subject Analysis',
      description: 'Detailed subject performance analysis within a specific grade',
      icon: Book,
      color: 'bg-green-50 border-green-200 text-green-700',
      supportsComparison: true
    },
    {
      id: 'learner-subject',
      label: 'Individual Learner (Subject)',
      description: 'Individual student performance analysis within a specific subject',
      icon: UserCheck,
      color: 'bg-purple-50 border-purple-200 text-purple-700',
      supportsComparison: true
    },
    {
      id: 'learner-overall',
      label: 'Individual Learner (Overall)',
      description: 'Comprehensive individual student performance across all subjects',
      icon: BarChart3,
      color: 'bg-orange-50 border-orange-200 text-orange-700',
      supportsComparison: false
    },
    {
      id: 'task-comparison',
      label: 'Task Comparison',
      description: 'Compare performance between two specific assessment tasks',
      icon: ArrowLeftRight,
      color: 'bg-indigo-50 border-indigo-200 text-indigo-700',
      supportsComparison: true,
      requiresComparison: true
    }
  ], []);

  // Load available tasks when grade/subject changes
  const extractGradeNumber = (gradeSelection) => {
    if (!gradeSelection) return '';
    if (gradeSelection === 'Grade R') return 'R';
    return gradeSelection.replace('Grade ', '');
  };

  const loadAvailableTasks = useCallback(async () => {
    if (!selectedGrade) return;

    try {
      const allTasks = await AssessmentService.getAssessmentTasks();
      const gradeNumber = extractGradeNumber(selectedGrade);
      
      let filteredTasks = allTasks.filter(t => t.grade === gradeNumber);
      
      if (selectedSubject) {
        filteredTasks = filteredTasks.filter(task => task.subject === selectedSubject);
      }
      
      setAvailableTasks(filteredTasks);
    } catch (error) {
      console.error('Error loading tasks:', error);
    }
  }, [selectedGrade, selectedSubject]);


  // Load available learners
  const loadAvailableLearners = useCallback(async () => {
    if (!selectedGrade || (reportType !== 'learner-subject' && reportType !== 'learner-overall')) {
      return;
    }

    try {
      setLoadingLearners(true);
      setError(null);
      
      const allTasks = await AssessmentService.getAssessmentTasks();
      let relevantTasks = allTasks.filter(task => task.grade === selectedGrade);
      
      if (reportType === 'learner-subject' && selectedSubject) {
        relevantTasks = relevantTasks.filter(task => task.subject === selectedSubject);
      }

      const learnerMap = new Map();
      
      for (const task of relevantTasks) {
        try {
          const marks = await AssessmentService.getLearnerMarks(task.id);
          marks.forEach(mark => {
            const learnerKey = `${mark.learnerSurname}|${mark.learnerName}`;
            if (!learnerMap.has(learnerKey)) {
              learnerMap.set(learnerKey, {
                key: learnerKey,
                surname: mark.learnerSurname,
                name: mark.learnerName,
                school: mark.school || 'School Not Specified',
                fullName: `${mark.learnerSurname}, ${mark.learnerName}`,
                displayName: `${mark.learnerSurname}, ${mark.learnerName} (${mark.school || 'School Not Specified'})`
              });
            }
          });
        } catch (error) {
          console.warn(`Error loading marks for task ${task.id}:`, error);
        }
      }

      const learners = Array.from(learnerMap.values())
        .sort((a, b) => a.school.localeCompare(b.school) || a.fullName.localeCompare(b.fullName));

      setAvailableLearners(learners);
    } catch (error) {
      console.error('Error loading learners:', error);
      setError('Failed to load learners');
    } finally {
      setLoadingLearners(false);
    }
  }, [selectedGrade, selectedSubject, reportType]);

  // Effects
  React.useEffect(() => {
    loadAvailableTasks();
  }, [loadAvailableTasks]);

  React.useEffect(() => {
    loadAvailableLearners();
  }, [loadAvailableLearners]);

  // Validation
  const validationResult = useMemo(() => {
    if (!reportType) {
      return { isValid: false, message: 'Please select a report type' };
    }
    if (!selectedGrade) {
      return { isValid: false, message: 'Please select a grade' };
    }
    if ((reportType === 'subject' || reportType === 'learner-subject' || reportType === 'task-comparison') && !selectedSubject) {
      return { isValid: false, message: 'Please select a subject' };
    }
    if ((reportType === 'learner-subject' || reportType === 'learner-overall') && !selectedLearner) {
      return { isValid: false, message: 'Please select a learner' };
    }
    if (reportType === 'task-comparison') {
      if (!comparisonTask1 || !comparisonTask2) {
        return { isValid: false, message: 'Please select two tasks to compare' };
      }
      if (comparisonTask1 === comparisonTask2) {
        return { isValid: false, message: 'Please select different tasks to compare' };
      }
      if (comparisonScope === 'learner' && !selectedLearner) {
        return { isValid: false, message: 'Please select a learner for individual comparison' };
      }
    }
    return { isValid: true, message: null };
  }, [reportType, selectedGrade, selectedSubject, selectedLearner, comparisonTask1, comparisonTask2, comparisonScope]);

  // Generate report
  const generateReport = async () => {
    if (!validationResult.isValid) return;

    setLoading(true);
    setError(null);

    try {
      let report;
      
      switch (reportType) {
        case 'grade':
          report = await ReportsService.generateGradeReport(selectedGrade, userProfile, reportContext);
          break;
        case 'subject':
          report = await ReportsService.generateSubjectReport(selectedGrade, selectedSubject, userProfile, reportContext);
          break;
        case 'learner-subject':
          const [surname, name] = selectedLearner.split('|');
          report = await ReportsService.generateLearnerSubjectReport(surname, name, selectedGrade, selectedSubject, userProfile, reportContext);
          break;
        case 'learner-overall':
          const [lSurname, lName] = selectedLearner.split('|');
          report = await ReportsService.generateLearnerOverallReport(lSurname, lName, selectedGrade, userProfile, reportContext);
          break;
        case 'task-comparison':
          report = await ReportsService.generateTaskComparisonReport(
            comparisonTask1, 
            comparisonTask2, 
            comparisonScope, 
            selectedLearner, 
            userProfile, 
            reportContext
          );
          break;
        default:
          throw new Error('Invalid report type selected');
      }

      setGeneratedReport(report);
    } catch (error) {
      console.error('Error generating report:', error);
      setError(error.message || 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = useCallback(() => {
    setReportType('');
    setSelectedGrade('');
    setSelectedSubject('');
    setSelectedLearner('');
    setReportContext('');
    setEnableComparison(false);
    setComparisonTask1('');
    setComparisonTask2('');
    setComparisonScope('learner');
    setGeneratedReport(null);
    setError(null);
    setSearchTerm('');
    setAvailableLearners([]);
    setAvailableTasks([]);
  }, []);

  const filteredLearners = useMemo(() => {
    return availableLearners.filter(learner =>
      learner.fullName.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [availableLearners, searchTerm]);

  const selectedReportType = reportTypes.find(type => type.id === reportType);

  if (generatedReport) {
    return (
      <ReportDisplay 
        report={generatedReport} 
        onBack={() => setGeneratedReport(null)}
        onGenerateNew={resetForm}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <div className="flex items-center gap-3 mb-4">
          <Brain className="w-6 h-6 text-blue-600" />
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Enhanced AI Reports Generator</h1>
        </div>
        <p className="text-sm sm:text-base text-gray-600">
          Generate comprehensive performance reports with context and comparison capabilities using advanced AI analysis.
        </p>
      </div>

      {/* Report Configuration Form */}
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Report Configuration</h2>
        
        {/* Report Type Selection - Responsive Grid */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">Report Type</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {reportTypes.map((type) => {
                const Icon = type.icon;
                return (
                  <button
                    key={type.id}
                    onClick={() => {
                      setReportType(type.id);
                      setEnableComparison(type.requiresComparison || false);
                      if (type.requiresComparison) {
                        setComparisonScope('grade');
                      }
                    }}
                    className={`
                      p-4 text-left border-2 rounded-lg transition-all hover:shadow-md
                      ${reportType === type.id 
                        ? `${type.color} border-current shadow-sm` 
                        : 'border-gray-200 hover:border-gray-300'
                      }
                    `}
                  >
                    <div className="flex items-start gap-3">
                      <Icon className={`w-5 h-5 mt-0.5 ${reportType === type.id ? 'text-current' : 'text-gray-400'}`} />
                      <div>
                        <h3 className={`font-medium text-sm ${reportType === type.id ? 'text-current' : 'text-gray-900'}`}>
                          {type.label}
                        </h3>
                        <p className={`text-xs mt-1 ${reportType === type.id ? 'text-current opacity-80' : 'text-gray-500'}`}>
                          {type.description}
                        </p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Parameters Section - Responsive Rows */}
          {reportType && (
            <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
              <h3 className="font-medium text-gray-900">Report Parameters</h3>
              
              {/* Grade and Subject Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Grade</label>
                  <select
                    value={selectedGrade}
                    onChange={(e) => setSelectedGrade(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  >
                    <option value="">Select Grade</option>
                    {grades.map(grade => (
                      <option key={grade} value={grade}>{grade}</option>
                    ))}
                  </select>
                </div>

                {(reportType === 'subject' || reportType === 'learner-subject' || reportType === 'task-comparison') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
                    <select
                      value={selectedSubject}
                      onChange={(e) => setSelectedSubject(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    >
                      <option value="">Select Subject</option>
                      {subjects.map(subject => (
                        <option key={subject} value={subject}>{subject}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {/* Learner Selection */}
              {(reportType === 'learner-subject' || reportType === 'learner-overall' || (reportType === 'task-comparison' && comparisonScope === 'learner')) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Learner
                    {loadingLearners && <span className="text-blue-500 ml-2">(Loading...)</span>}
                  </label>
                  {availableLearners.length > 10 && (
                    <input
                      type="text"
                      placeholder="Search learners..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full px-3 py-2 mb-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                  )}
                  <select
                    value={selectedLearner}
                    onChange={(e) => setSelectedLearner(e.target.value)}
                    disabled={loadingLearners}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-100"
                  >
                    <option value="">Select Learner</option>
                    {filteredLearners.map(learner => (
                      <option key={learner.key} value={learner.key}>
                        {learner.fullName}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Task Comparison Section */}
              {reportType === 'task-comparison' && (
                <div className="space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center gap-2">
                    <ArrowLeftRight className="w-5 h-5 text-blue-600" />
                    <h4 className="font-medium text-blue-900">Task Comparison Settings</h4>
                  </div>

                  {/* Comparison Scope */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Comparison Scope</label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <button
                        onClick={() => setComparisonScope('learner')}
                        className={`p-3 text-left border rounded-md transition-colors ${
                          comparisonScope === 'learner' 
                            ? 'border-blue-500 bg-blue-50 text-blue-700' 
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <UserCheck className="w-4 h-4" />
                          <span className="font-medium text-sm">Individual Learner</span>
                        </div>
                        <p className="text-xs text-gray-600 mt-1">Compare a specific learner's performance between two tasks</p>
                      </button>
                      <button
                        onClick={() => setComparisonScope('grade')}
                        className={`p-3 text-left border rounded-md transition-colors ${
                          comparisonScope === 'grade' 
                            ? 'border-blue-500 bg-blue-50 text-blue-700' 
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          <span className="font-medium text-sm">Grade Level</span>
                        </div>
                        <p className="text-xs text-gray-600 mt-1">Compare overall grade performance between two tasks</p>
                      </button>
                    </div>
                  </div>

                  {/* Side-by-Side Card-Style Task Selection with Summary */}
                  <div className="space-y-4">
                    {/* Selection Summary */}
                    {(comparisonTask1 || comparisonTask2) && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h4 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
                          <ArrowLeftRight className="w-4 h-4" />
                          Selected Tasks for Comparison
                        </h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="font-medium text-blue-800">Task 1:</span>
                            <span className="ml-2 text-blue-700">
                              {comparisonTask1 
                                ? availableTasks.find(t => t.id === comparisonTask1)?.name || 'Unknown'
                                : 'Not selected'
                              }
                            </span>
                          </div>
                          <div>
                            <span className="font-medium text-blue-800">Task 2:</span>
                            <span className="ml-2 text-blue-700">
                              {comparisonTask2 
                                ? availableTasks.find(t => t.id === comparisonTask2)?.name || 'Unknown'
                                : 'Not selected'
                              }
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Task Selection Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* First Task Column */}
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <label className="block text-sm font-medium text-gray-700">
                            First Task
                          </label>
                          {comparisonTask1 && (
                            <button
                              onClick={() => setComparisonTask1('')}
                              className="text-xs text-red-600 hover:text-red-800 flex items-center gap-1"
                            >
                              <X className="w-3 h-3" />
                              Clear
                            </button>
                          )}
                        </div>
                        <div className="border border-gray-200 rounded-lg p-2 bg-gray-50 max-h-80 overflow-y-auto">
                          <div className="space-y-2">
                            {availableTasks.length === 0 ? (
                              <div className="text-center py-8 text-gray-500 text-sm">
                                No tasks available for selected criteria
                              </div>
                            ) : (
                              availableTasks.map(task => (
                                <button
                                  key={task.id}
                                  onClick={() => setComparisonTask1(task.id)}
                                  disabled={task.id === comparisonTask2}
                                  className={`w-full p-3 text-left border rounded-md transition-all duration-200 ${
                                    comparisonTask1 === task.id 
                                      ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-sm' 
                                      : task.id === comparisonTask2
                                      ? 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                                      : 'border-gray-300 hover:border-gray-400 hover:bg-white hover:shadow-sm bg-white cursor-pointer'
                                  }`}
                                >
                                  <div className="font-medium text-sm mb-1">{task.name}</div>
                                  <div className="text-xs text-gray-500 space-y-1">
                                    <div className="flex items-center gap-2">
                                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                                        {task.type}
                                      </span>
                                      <span className="text-gray-600">{task.totalMarks} marks</span>
                                    </div>
                                    <div className="text-gray-500">
                                      {new Date(task.createdAt).toLocaleDateString()}
                                    </div>
                                  </div>
                                  {task.topics && task.topics.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mt-2">
                                      {task.topics.slice(0, 2).map((topic, index) => (
                                        <span key={index} className="inline-block px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">
                                          {topic}
                                        </span>
                                      ))}
                                      {task.topics.length > 2 && (
                                        <span className="inline-block px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                                          +{task.topics.length - 2}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                  {task.id === comparisonTask2 && (
                                    <div className="text-xs text-gray-500 mt-1 italic">
                                      Selected as Task 2
                                    </div>
                                  )}
                                </button>
                              ))
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Second Task Column */}
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <label className="block text-sm font-medium text-gray-700">
                            Second Task
                          </label>
                          {comparisonTask2 && (
                            <button
                              onClick={() => setComparisonTask2('')}
                              className="text-xs text-red-600 hover:text-red-800 flex items-center gap-1"
                            >
                              <X className="w-3 h-3" />
                              Clear
                            </button>
                          )}
                        </div>
                        <div className="border border-gray-200 rounded-lg p-2 bg-gray-50 max-h-80 overflow-y-auto">
                          <div className="space-y-2">
                            {availableTasks.filter(task => task.id !== comparisonTask1).length === 0 ? (
                              <div className="text-center py-8 text-gray-500 text-sm">
                                {comparisonTask1 
                                  ? 'Select a different task from Task 1' 
                                  : 'Please select Task 1 first'
                                }
                              </div>
                            ) : (
                              availableTasks.filter(task => task.id !== comparisonTask1).map(task => (
                                <button
                                  key={task.id}
                                  onClick={() => setComparisonTask2(task.id)}
                                  className={`w-full p-3 text-left border rounded-md transition-all duration-200 ${
                                    comparisonTask2 === task.id 
                                      ? 'border-green-500 bg-green-50 text-green-700 shadow-sm' 
                                      : 'border-gray-300 hover:border-gray-400 hover:bg-white hover:shadow-sm bg-white cursor-pointer'
                                  }`}
                                >
                                  <div className="font-medium text-sm mb-1">{task.name}</div>
                                  <div className="text-xs text-gray-500 space-y-1">
                                    <div className="flex items-center gap-2">
                                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                                        {task.type}
                                      </span>
                                      <span className="text-gray-600">{task.totalMarks} marks</span>
                                    </div>
                                    <div className="text-gray-500">
                                      {new Date(task.createdAt).toLocaleDateString()}
                                    </div>
                                  </div>
                                  {task.topics && task.topics.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mt-2">
                                      {task.topics.slice(0, 2).map((topic, index) => (
                                        <span key={index} className="inline-block px-1.5 py-0.5 text-xs bg-green-100 text-green-700 rounded">
                                          {topic}
                                        </span>
                                      ))}
                                      {task.topics.length > 2 && (
                                        <span className="inline-block px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                                          +{task.topics.length - 2}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </button>
                              ))
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Report Context */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <MessageSquare className="w-4 h-4 inline mr-1" />
                  Report Context (Optional)
                </label>
                <textarea
                  value={reportContext}
                  onChange={(e) => setReportContext(e.target.value)}
                  placeholder="Provide additional context for the AI to consider when generating the report. For example: 'Focus on identifying struggling learners', 'Include recommendations for parent meetings', 'Emphasize curriculum alignment', etc."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  rows={3}
                />
                <p className="text-xs text-gray-500 mt-1">
                  This context will help the AI generate more targeted and relevant insights for your specific needs.
                </p>
              </div>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Validation Message */}
          {!validationResult.isValid && (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
              <p className="text-sm text-yellow-700">{validationResult.message}</p>
            </div>
          )}

          {/* Generate Button */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <button
              onClick={generateReport}
              disabled={!validationResult.isValid || loading}
              className="flex-1 sm:flex-none px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm font-medium"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Generating Report...
                </>
              ) : (
                <>
                  <Brain className="w-4 h-4" />
                  Generate AI Report
                </>
              )}
            </button>

            {reportType && (
              <button
                onClick={resetForm}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 text-sm"
              >
                Reset Form
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Report Preview Info */}
      {selectedReportType && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h3 className="font-medium text-blue-800 mb-1">AI-Generated Report Preview</h3>
              <div className="text-sm text-blue-700 space-y-1">
                <p>{selectedReportType.description}</p>
                {reportContext && (
                  <p className="italic">Custom context: "{reportContext.slice(0, 100)}{reportContext.length > 100 ? '...' : ''}"</p>
                )}
                {reportType === 'task-comparison' && comparisonTask1 && comparisonTask2 && (
                  <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <h4 className="font-medium text-green-900 mb-2">Enhanced Analysis Features</h4>
                    <div className="text-sm text-green-700 space-y-1">
                      <p>✅ Complete task structure analysis (topics, subtopics, concepts)</p>
                      <p>✅ Marks allocation breakdown by topic</p>
                      <p>✅ Taxonomy/difficulty level comparison</p>
                      <p>✅ Topic-specific performance insights</p>
                      <p>✅ Detailed curriculum coverage analysis</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedReportsGenerator;